import { Injectable, Inject, NgZone } from "@angular/core";
import { HubConnection } from "@microsoft/signalr";
import { Store } from "@ngxs/store";
import { UserState } from "../state/user/user-state";
import { ApiService } from "./api.service";
import { SignalingService } from "./hub.service";
import { MESSAGING_ENDPOINT, BASE_URL } from "./injection-tokens";
import { distinctUntilChanged, filter, Observable } from "rxjs";
import { ClientMessageHeader } from "../models/client-message";
import { Messages } from "../state/messages/message-actions";
import { MessageService } from "primeng/api";
import { User } from "../models/user";

export abstract class MessagingServiceBase {
    abstract getMessages(): Observable<ClientMessageHeader[]>
    abstract markMessageAsRead(message: ClientMessageHeader): ClientMessageHeader
    abstract receiveClientMessage(message: ClientMessageHeader): boolean
    abstract markMessageAsDelivered(message: ClientMessageHeader): ClientMessageHeader
}

@Injectable({ providedIn: 'root' })
export class MessagingService extends MessagingServiceBase {
    //State observable for when current user is logged in
    currentUser$ = this._store.select(UserState.currentUser);
    private messagingHub: HubConnection;

    constructor
        (
            private _store: Store,
            private _api: ApiService,
            private _signaling: SignalingService,
            private _messages: MessageService,
            private zone: NgZone,
            @Inject(MESSAGING_ENDPOINT) private messagingEndpoint: string,
            @Inject(BASE_URL) private baseUrl: string
        ) {
        super();
        this.messagingHub = this._signaling.generateHub(this.baseUrl + this.messagingEndpoint);

        //set up our hub connection when the user is officinally logged in
        this.currentUser$
            .pipe(
                filter((cu): cu is User => cu != null && cu.id != null),
                distinctUntilChanged((a, b) => a.id === b.id)
            )
            .subscribe
            (
                cu => {
                    if (cu?.id != null) {
                        this._signaling.startHub(this.messagingHub);
                    }
                    else {
                        this.messagingHub.stop().catch(err => console.error('Hub stop failed', err));
                    }
                }
            );
            
        //Handle inbound hub messages
        this.messagingHub.on("ReceiveClientMessage", (message: ClientMessageHeader) => this.receiveClientMessage(message));
    }

    override receiveClientMessage(message: ClientMessageHeader): boolean {
        this.zone.run(() => { // Ensures Angualar detects change making the toast appear imediately
            console.log(message);
            this.markMessageAsDelivered(message);
            this._store.dispatch(new Messages.NewMessageReceived({ message: message }));
            this._messages.add({ key: 'main-alert', severity: 'info', summary: 'New Notification', detail: message.title });
        });
        return true;
    }

    override markMessageAsDelivered(message: ClientMessageHeader): ClientMessageHeader {
        this.messagingHub.invoke("ConfirmMessageDelivery", message.id);
        this._store.dispatch(new Messages.MessageDeliveryConfirmed({ message: message }));
        return message;
    }

    override getMessages(): Observable<ClientMessageHeader[]> {
        return this._api.makeAPIGetCall<ClientMessageHeader[]>(this.baseUrl, "/util/messages", { includeRead: "true", includeUndelivered: "true", includeUnread: "true" }, {});
    }

    override markMessageAsRead(message: ClientMessageHeader): ClientMessageHeader {
        this.messagingHub.invoke("MarkMessageRead", message.id);
        this._store.dispatch(new Messages.MessageRead({ message: message }));
        return message;
    }
}   